import { useState, useRef } from 'react'
import {
  useTemplates,
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  useSetDefaultTemplate,
  useUploadTemplateAsset,
  usePreviewTemplate,
  type Template,
  type CreateTemplateRequest,
} from '@/hooks/useTemplates'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { FileCode, Star, Pencil, Trash2, Upload, Eye, Plus } from 'lucide-react'
import { toast } from 'sonner'

export function TemplatesListPage() {
  const { data, isLoading } = useTemplates()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const templates = data?.templates || []

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold">Plantillas PDF</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Plantillas PDF</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gestiona las plantillas HTML para reportes PDF
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Crear plantilla
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileCode className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">No hay plantillas configuradas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onEdit={() => {
                setSelectedId(t.id)
                setEditOpen(true)
              }}
              onDelete={() => setDeleteConfirmId(t.id)}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <CreateTemplateDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      {/* Edit Dialog */}
      {selectedId && (
        <TemplateEditDialog
          templateId={selectedId}
          open={editOpen}
          onClose={() => {
            setEditOpen(false)
            setSelectedId(null)
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <DeleteConfirmDialog
          templateId={deleteConfirmId}
          templateName={templates.find((t) => t.id === deleteConfirmId)?.name || ''}
          open={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
        />
      )}
    </div>
  )
}

// ========================================
// Create Template Dialog
// ========================================

function CreateTemplateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createTemplate = useCreateTemplate()
  const [form, setForm] = useState<CreateTemplateRequest>({
    name: '',
    slug: '',
    description: '',
    engine: 'handlebars',
    recipe: 'chrome-pdf',
    html_content: '',
  })

  const handleCreate = async () => {
    if (!form.name || !form.slug) {
      toast.error('Nombre y slug son requeridos')
      return
    }
    await createTemplate.mutateAsync(form)
    setForm({
      name: '',
      slug: '',
      description: '',
      engine: 'handlebars',
      recipe: 'chrome-pdf',
      html_content: '',
    })
    onClose()
  }

  const autoSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Crear plantilla</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Nombre</Label>
            <Input
              placeholder="Reporte Ejecutivo"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value
                setForm((f) => ({
                  ...f,
                  name,
                  slug: f.slug === autoSlug(f.name) || !f.slug ? autoSlug(name) : f.slug,
                }))
              }}
            />
          </div>
          <div className="grid gap-2">
            <Label>Slug</Label>
            <Input
              placeholder="reporte-ejecutivo"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Identificador unico, no se puede cambiar despues
            </p>
          </div>
          <div className="grid gap-2">
            <Label>Descripcion</Label>
            <Input
              placeholder="Descripcion de la plantilla..."
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Motor</Label>
              <Input value={form.engine} disabled />
            </div>
            <div className="grid gap-2">
              <Label>Receta</Label>
              <Input value={form.recipe} disabled />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>HTML inicial (opcional)</Label>
            <textarea
              className="h-32 w-full resize-y rounded-md border bg-muted/50 p-3 font-mono text-xs"
              placeholder="<html>...</html>"
              value={form.html_content}
              onChange={(e) => setForm((f) => ({ ...f, html_content: e.target.value }))}
              spellCheck={false}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={createTemplate.isPending || !form.name || !form.slug}
          >
            {createTemplate.isPending ? 'Creando...' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ========================================
// Template Card
// ========================================

function TemplateCard({
  template,
  onEdit,
  onDelete,
}: {
  template: Template
  onEdit: () => void
  onDelete: () => void
}) {
  const setDefault = useSetDefaultTemplate()

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base">{template.name}</CardTitle>
          <div className="flex gap-1">
            {template.is_default && (
              <Badge variant="default" className="text-xs">
                Default
              </Badge>
            )}
            <Badge variant={template.is_active ? 'outline' : 'secondary'} className="text-xs">
              {template.is_active ? 'Activo' : 'Inactivo'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{template.description || 'Sin descripcion'}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            Slug: <code className="rounded bg-muted px-1 py-0.5">{template.slug}</code>
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Motor: {template.engine}</span>
          <span>|</span>
          <span>Receta: {template.recipe}</span>
        </div>

        <div className="flex gap-2 border-t pt-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1 h-3 w-3" />
            Editar
          </Button>
          {!template.is_default && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDefault.mutate(template.id)}
                disabled={setDefault.isPending}
              >
                <Star className="mr-1 h-3 w-3" />
                Default
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Eliminar
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ========================================
// Template Edit Dialog
// ========================================

function TemplateEditDialog({
  templateId,
  open,
  onClose,
}: {
  templateId: string
  open: boolean
  onClose: () => void
}) {
  const { data: template, isLoading } = useTemplate(templateId)
  const updateTemplate = useUpdateTemplate()
  const uploadAsset = useUploadTemplateAsset()
  const previewTemplate = usePreviewTemplate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [assetKey, setAssetKey] = useState<string>('')

  const [formState, setFormState] = useState<{
    name?: string
    description?: string
    html_content?: string
    is_active?: boolean
  }>({})

  // Initialize form when template loads
  const name = formState.name ?? template?.name ?? ''
  const description = formState.description ?? template?.description ?? ''
  const htmlContent = formState.html_content ?? template?.html_content ?? ''
  const isActive = formState.is_active ?? template?.is_active ?? true

  const handleSave = () => {
    const updates: Record<string, unknown> = {}
    if (formState.name !== undefined) updates.name = formState.name
    if (formState.description !== undefined) updates.description = formState.description
    if (formState.html_content !== undefined) updates.html_content = formState.html_content
    if (formState.is_active !== undefined) updates.is_active = formState.is_active

    if (Object.keys(updates).length === 0) {
      toast.info('No hay cambios para guardar')
      return
    }

    updateTemplate.mutate({ id: templateId, updates }, { onSuccess: () => onClose() })
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !assetKey) return

    if (file.size > 500 * 1024) {
      toast.error('El archivo no puede superar 500KB')
      return
    }

    uploadAsset.mutate({ id: templateId, assetKey, file })
    e.target.value = ''
  }

  const handlePreview = () => {
    previewTemplate.mutate(templateId, {
      onSuccess: (data) => {
        const win = window.open('', '_blank')
        if (win) {
          win.document.write(data.html)
          win.document.close()
        }
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isLoading ? 'Cargando...' : `Editar: ${template?.name}`}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="html">HTML Editor</TabsTrigger>
              <TabsTrigger value="assets">Assets</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripcion</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setFormState((s) => ({ ...s, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input value={template?.slug || ''} disabled />
                <p className="text-xs text-muted-foreground">El slug no se puede cambiar</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={isActive}
                  onCheckedChange={(v) => setFormState((s) => ({ ...s, is_active: v }))}
                />
                <Label>Activo</Label>
              </div>
              <div className="space-y-2">
                <Label>Campos personalizables</Label>
                <div className="flex flex-wrap gap-2">
                  {template?.customizable_fields?.map((f) => (
                    <Badge key={f} variant="outline">
                      {f}
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* HTML Editor Tab */}
            <TabsContent value="html" className="space-y-4">
              <div className="space-y-2">
                <Label>Contenido HTML (Handlebars)</Label>
                <textarea
                  className="h-[500px] w-full resize-y rounded-md border bg-muted/50 p-3 font-mono text-xs"
                  value={htmlContent}
                  onChange={(e) => setFormState((s) => ({ ...s, html_content: e.target.value }))}
                  spellCheck={false}
                />
                <p className="text-xs text-muted-foreground">
                  Usa placeholders como {'{{BRANDING_LOGO}}'}, {'{{BRANDING_COMPANY_NAME}}'},{' '}
                  {'{{ECHARTS_JS}}'}, etc.
                </p>
              </div>
            </TabsContent>

            {/* Assets Tab */}
            <TabsContent value="assets" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Assets embebidos en este template. Sube imagenes (max 500KB, JPG/PNG/SVG).
              </p>
              <div className="space-y-4">
                {['logo', 'network_image'].map((key) => (
                  <Card key={key}>
                    <CardContent className="flex items-center justify-between py-4">
                      <div>
                        <p className="text-sm font-medium">{key}</p>
                        <p className="text-xs text-muted-foreground">
                          {template?.embedded_assets_keys?.includes(key)
                            ? 'Configurado'
                            : 'No configurado'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setAssetKey(key)
                          fileInputRef.current?.click()
                        }}
                        disabled={uploadAsset.isPending}
                      >
                        <Upload className="mr-1 h-3 w-3" />
                        Subir
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/svg+xml"
                className="hidden"
                onChange={handleFileSelect}
              />
            </TabsContent>

            {/* Preview Tab */}
            <TabsContent value="preview" className="space-y-4">
              <Button onClick={handlePreview} disabled={previewTemplate.isPending}>
                <Eye className="mr-2 h-4 w-4" />
                {previewTemplate.isPending ? 'Generando...' : 'Generar Preview'}
              </Button>
              <p className="text-sm text-muted-foreground">
                Genera un preview del HTML resuelto (sin datos reales) en una nueva ventana.
              </p>
            </TabsContent>
          </Tabs>
        )}

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updateTemplate.isPending}>
            {updateTemplate.isPending ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ========================================
// Delete Confirmation Dialog
// ========================================

function DeleteConfirmDialog({
  templateId,
  templateName,
  open,
  onClose,
}: {
  templateId: string
  templateName: string
  open: boolean
  onClose: () => void
}) {
  const deleteTemplate = useDeleteTemplate()

  const handleDelete = () => {
    deleteTemplate.mutate(templateId, {
      onSuccess: () => onClose(),
    })
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Eliminar Template</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Estas seguro de eliminar <strong>{templateName}</strong>? Esta accion no se puede
          deshacer.
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteTemplate.isPending}>
            {deleteTemplate.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
